import React from "react";

// Filenames the backend uses for crawl artifacts. Never user-facing.
const ARTIFACT_EXT_RE = /\.(json|jsonl|ndjson|yaml|yml)$/i;

function isHttpUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

function prettifyUrl(url) {
  try {
    const u = new URL(url);
    const path = u.pathname && u.pathname !== "/" ? u.pathname : "";
    return (u.hostname + path).replace(/\/$/, "");
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

function formatPages(citation) {
  const { page_start, page_end } = citation;
  if (!page_start && !page_end) return null;
  if (page_start && page_end && page_start !== page_end) {
    return `pages ${page_start}–${page_end}`;
  }
  return `page ${page_start || page_end}`;
}

function citationHref(citation) {
  if (isHttpUrl(citation.external_url)) return citation.external_url.trim();
  if (isHttpUrl(citation.url)) return citation.url.trim();
  return null;
}

function citationLabel(citation) {
  // 1. Real web page URL wins — that's what users actually want to see.
  if (isHttpUrl(citation.external_url)) {
    return prettifyUrl(citation.external_url);
  }
  // 2. A user-facing filename (PDF, docx, etc.) is fine.
  if (
    citation.original_filename &&
    !ARTIFACT_EXT_RE.test(citation.original_filename)
  ) {
    return citation.original_filename;
  }
  // 3. Fall back to the raw url, stripped of protocol.
  if (citation.url) return prettifyUrl(citation.url);
  // 4. Last resort.
  return citation.original_filename || "Source";
}

// Hide citations that have no user-meaningful target. A bare crawl-artifact
// filename with no URL would otherwise render as a dead card.
function isDisplayable(citation) {
  if (citationHref(citation)) return true;
  if (
    citation.original_filename &&
    !ARTIFACT_EXT_RE.test(citation.original_filename)
  ) {
    return true;
  }
  return false;
}

const CitationsBar = ({ citations }) => {
  const visible = (citations || []).filter(isDisplayable);
  if (visible.length === 0) return null;

  const openCitation = (citation) => {
    const url = citationHref(citation);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="md-citations">
      <div className="md-citations-label">Sources</div>
      <div className="md-citations-strip">
        {visible.map((c, i) => {
          const pages = formatPages(c);
          const label = citationLabel(c);
          const href = citationHref(c);
          const clickable = !!href;
          return (
            <div
              key={i}
              id={`cite-${i + 1}`}
              className={`md-citation-card${clickable ? "" : " md-citation-card--disabled"}`}
              onClick={clickable ? () => openCitation(c) : undefined}
              onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCitation(c);
                }
              }}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : -1}
              title={href || label}
            >
              <div className="md-citation-num">{i + 1}</div>
              <div className="md-citation-body">
                <div className="md-citation-title">{label}</div>
                {pages && <div className="md-citation-meta">{pages}</div>}
              </div>
              {clickable && (
                <span className="material-icons md-citation-icon">open_in_new</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CitationsBar;
